// src/services/cacheService.js
// Client-side caching service with TTL, persistence, request deduplication,
// IndexedDB support for admin data, and stale-while-revalidate

import {
  idbGet, idbSet, idbDelete, idbDeleteByPrefix,
  idbClear, idbCleanupExpired,
} from './indexedDBStore';

const CACHE_PREFIX = 'cm_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default

// In-memory cache for fastest access
const memoryCache = new Map();

// Track in-flight requests to prevent duplicates
const pendingRequests = new Map();

// Cache configuration for different data types
const CACHE_CONFIG = {
  // Static data - rarely changes (1 hour TTL)
  masterServices: { ttl: 60 * 60 * 1000, persist: true },
  serviceCategories: { ttl: 60 * 60 * 1000, persist: true },

  // Semi-static data - changes occasionally (15 minutes TTL)
  garageProfile: { ttl: 15 * 60 * 1000, persist: true },
  garageServices: { ttl: 15 * 60 * 1000, persist: true },
  garageSettings: { ttl: 15 * 60 * 1000, persist: true },

  // Dynamic data - changes frequently (5 minutes TTL)
  customers: { ttl: 5 * 60 * 1000, persist: false },
  vehicles: { ttl: 5 * 60 * 1000, persist: false },
  quotes: { ttl: 2 * 60 * 1000, persist: false },
  jobs: { ttl: 2 * 60 * 1000, persist: false },
  appointments: { ttl: 2 * 60 * 1000, persist: false },

  // User-specific data (10 minutes TTL)
  clientProfile: { ttl: 10 * 60 * 1000, persist: true },
  savedGarages: { ttl: 10 * 60 * 1000, persist: false },

  // Search results (2 minutes TTL)
  searchResults: { ttl: 2 * 60 * 1000, persist: false },

  // Stats and analytics (1 minute TTL)
  stats: { ttl: 60 * 1000, persist: false },

  // ── Admin data - persisted to IndexedDB (survives browser close) ──
  adminGarages:       { ttl: 4 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  territoryMapData:   { ttl: 2 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminClients:       { ttl: 4 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminUsers:         { ttl: 2 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminGarageStats:   { ttl: 1 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminClientStats:   { ttl: 1 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminSubscriptions: { ttl: 2 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminClaims:        { ttl: 30 * 60 * 1000,     persist: true, storage: 'indexeddb' },
  adminQuotes:        { ttl: 30 * 60 * 1000,     persist: true, storage: 'indexeddb' },
  adminConversations: { ttl: 15 * 60 * 1000,     persist: true, storage: 'indexeddb' },
  adminAnalytics:     { ttl: 1 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminDataQuality:   { ttl: 4 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminCRMMetrics:    { ttl: 1 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
  adminPipeline:      { ttl: 30 * 60 * 1000,     persist: true, storage: 'indexeddb' },
  adminCommissions:   { ttl: 1 * 60 * 60 * 1000, persist: true, storage: 'indexeddb' },
};

/**
 * Generate a cache key from type and optional params
 */
function generateKey(type, params = null) {
  if (!params) return `${CACHE_PREFIX}${type}`;

  const paramString = typeof params === 'string'
    ? params
    : JSON.stringify(params, Object.keys(params).sort());

  return `${CACHE_PREFIX}${type}_${hashString(paramString)}`;
}

/**
 * Simple string hash for cache keys
 */
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Get TTL for a cache type
 */
function getTTL(type) {
  return CACHE_CONFIG[type]?.ttl || DEFAULT_TTL;
}

/**
 * Check if type should persist to localStorage
 */
function shouldPersist(type) {
  return CACHE_CONFIG[type]?.persist || false;
}

/**
 * Check if type uses IndexedDB storage
 */
function useIndexedDB(type) {
  return CACHE_CONFIG[type]?.storage === 'indexeddb';
}

/**
 * Set a value in cache
 */
function set(type, data, params = null, customTTL = null) {
  const key = generateKey(type, params);
  const ttl = customTTL || getTTL(type);
  const expiresAt = Date.now() + ttl;

  const cacheEntry = {
    data,
    expiresAt,
    type,
    params,
    cachedAt: Date.now(),
  };

  // Store in memory
  memoryCache.set(key, cacheEntry);

  // Persist to IndexedDB for admin types (fire-and-forget)
  if (useIndexedDB(type)) {
    idbSet(key, cacheEntry).catch(() => {});
  }
  // Persist to localStorage for non-IndexedDB types
  else if (shouldPersist(type)) {
    try {
      localStorage.setItem(key, JSON.stringify(cacheEntry));
    } catch (e) {
      // localStorage full or unavailable - just use memory cache
      console.warn('[Cache] localStorage write failed:', e.message);
    }
  }

  return data;
}

/**
 * Get a value from cache (synchronous - memory + localStorage only)
 * For IndexedDB types, use getAsync() instead
 */
function get(type, params = null) {
  const key = generateKey(type, params);

  // Check memory cache first (fastest)
  let entry = memoryCache.get(key);

  // If not in memory, check localStorage (not for IndexedDB types)
  if (!entry && shouldPersist(type) && !useIndexedDB(type)) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        entry = JSON.parse(stored);
        // Restore to memory cache for faster subsequent access
        memoryCache.set(key, entry);
      }
    } catch (e) {
      console.warn('[Cache] localStorage read failed:', e.message);
    }
  }

  // Check if entry exists and is not expired
  if (entry && entry.expiresAt > Date.now()) {
    return {
      data: entry.data,
      fromCache: true,
      cachedAt: entry.cachedAt,
      expiresAt: entry.expiresAt,
    };
  }

  // Entry expired or doesn't exist - clean up
  if (entry) {
    invalidate(type, params);
  }

  return null;
}

/**
 * Get a value from cache (async - supports IndexedDB)
 * Checks memory first, then IndexedDB for admin types, then localStorage
 */
async function getAsync(type, params = null) {
  // Try sync path first (memory + localStorage)
  const syncResult = get(type, params);
  if (syncResult) return syncResult;

  // For IndexedDB types, check persistent store
  if (useIndexedDB(type)) {
    const key = generateKey(type, params);
    const entry = await idbGet(key);
    if (entry && entry.expiresAt > Date.now()) {
      // Restore to memory cache for faster subsequent access
      memoryCache.set(key, entry);
      return {
        data: entry.data,
        fromCache: true,
        cachedAt: entry.cachedAt,
        expiresAt: entry.expiresAt,
      };
    }
    // Clean up expired entry
    if (entry) {
      idbDelete(key).catch(() => {});
    }
  }

  return null;
}

/**
 * Get any cached entry (even expired) - used by stale-while-revalidate
 * Async to support IndexedDB
 */
async function getAnyAsync(type, params = null) {
  const key = generateKey(type, params);

  // Check memory (including expired entries)
  const memEntry = memoryCache.get(key);
  if (memEntry) {
    return {
      data: memEntry.data,
      fromCache: true,
      cachedAt: memEntry.cachedAt,
      expiresAt: memEntry.expiresAt,
    };
  }

  // Check IndexedDB for admin types
  if (useIndexedDB(type)) {
    const entry = await idbGet(key);
    if (entry) {
      memoryCache.set(key, entry);
      return {
        data: entry.data,
        fromCache: true,
        cachedAt: entry.cachedAt,
        expiresAt: entry.expiresAt,
      };
    }
  }

  // Check localStorage for non-IndexedDB types
  if (shouldPersist(type) && !useIndexedDB(type)) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const entry = JSON.parse(stored);
        memoryCache.set(key, entry);
        return {
          data: entry.data,
          fromCache: true,
          cachedAt: entry.cachedAt,
          expiresAt: entry.expiresAt,
        };
      }
    } catch (e) { /* ignore */ }
  }

  return null;
}

/**
 * Check if cache entry exists and is valid
 */
function has(type, params = null) {
  return get(type, params) !== null;
}

/**
 * Invalidate a specific cache entry
 */
function invalidate(type, params = null) {
  const key = generateKey(type, params);

  memoryCache.delete(key);

  if (useIndexedDB(type)) {
    idbDelete(key).catch(() => {});
  } else if (shouldPersist(type)) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore
    }
  }
}

/**
 * Invalidate all entries of a specific type
 */
function invalidateType(type) {
  const prefix = `${CACHE_PREFIX}${type}`;

  // Clear from memory
  for (const key of memoryCache.keys()) {
    if (key.startsWith(prefix)) {
      memoryCache.delete(key);
    }
  }

  // Clear from IndexedDB for admin types
  if (useIndexedDB(type)) {
    idbDeleteByPrefix(prefix).catch(() => {});
  }

  // Clear from localStorage
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    // Ignore
  }
}

/**
 * Clear all cache entries (including IndexedDB)
 */
function clearAll() {
  memoryCache.clear();

  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    // Ignore
  }

  // Clear IndexedDB admin cache
  idbClear().catch(() => {});

  pendingRequests.clear();
}

/**
 * Execute a fetch function with caching and request deduplication
 * This is the main method for cached data fetching
 */
async function cachedFetch(type, fetchFn, params = null, options = {}) {
  const {
    forceRefresh = false,
    customTTL = null,
    onCacheHit = null,
    onCacheMiss = null,
  } = options;

  const key = generateKey(type, params);

  // Check cache first (unless force refresh) - use async for IndexedDB support
  if (!forceRefresh) {
    const cached = await getAsync(type, params);
    if (cached) {
      if (onCacheHit) onCacheHit(cached);
      return { ...cached, success: true };
    }
  }

  // Check if there's already a pending request for this key
  if (pendingRequests.has(key)) {
    // Wait for the existing request to complete
    return pendingRequests.get(key);
  }

  // Create new request promise
  const requestPromise = (async () => {
    try {
      if (onCacheMiss) onCacheMiss();

      const result = await fetchFn();

      if (result && (result.success !== false)) {
        // Cache the successful result
        const data = result.data !== undefined ? result.data : result;
        set(type, data, params, customTTL);

        return {
          data,
          success: true,
          fromCache: false,
          cachedAt: Date.now(),
        };
      }

      // Don't cache errors
      return {
        data: null,
        success: false,
        error: result?.error || 'Unknown error',
        fromCache: false,
      };
    } catch (error) {
      console.error(`[Cache] Fetch error for ${type}:`, error);
      return {
        data: null,
        success: false,
        error: error.message,
        fromCache: false,
      };
    } finally {
      // Remove from pending requests
      pendingRequests.delete(key);
    }
  })();

  // Store the pending request
  pendingRequests.set(key, requestPromise);

  return requestPromise;
}

/**
 * Stale-while-revalidate fetch: returns cached data instantly (even if stale),
 * triggers background refresh. For admin components that need instant page loads.
 */
async function cachedFetchSWR(type, fetchFn, params = null, options = {}) {
  const {
    customTTL = null,
    onCacheHit = null,
    onCacheMiss = null,
    onRevalidated = null,
  } = options;

  const key = generateKey(type, params);

  // Step 1: Try to get ANY cached data (even stale/expired)
  const cached = await getAnyAsync(type, params);

  if (cached) {
    const isStale = cached.expiresAt <= Date.now();

    if (!isStale) {
      // Fresh cache - return immediately, no background refresh needed
      if (onCacheHit) onCacheHit(cached);
      return {
        data: cached.data,
        success: true,
        fromCache: true,
        cachedAt: cached.cachedAt,
        isStale: false,
      };
    }

    // Stale cache - return stale data AND trigger background refresh
    if (onCacheHit) onCacheHit(cached);

    // Background refresh (fire-and-forget, deduped via pendingRequests)
    if (!pendingRequests.has(key)) {
      const refreshPromise = (async () => {
        try {
          const result = await fetchFn();
          if (result && result.success !== false) {
            const data = result.data !== undefined ? result.data : result;
            set(type, data, params, customTTL);
            if (onRevalidated) onRevalidated(data);
          }
        } catch (e) {
          console.warn(`[Cache] SWR background refresh failed for ${type}:`, e.message);
        } finally {
          pendingRequests.delete(key);
        }
      })();
      pendingRequests.set(key, refreshPromise);
    }

    return {
      data: cached.data,
      success: true,
      fromCache: true,
      cachedAt: cached.cachedAt,
      isStale: true,
    };
  }

  // No cache at all - must fetch (first time ever)
  if (onCacheMiss) onCacheMiss();
  return cachedFetch(type, fetchFn, params, { customTTL });
}

/**
 * Batch multiple cache fetches in parallel
 */
async function batchFetch(requests) {
  const results = await Promise.all(
    requests.map(({ type, fetchFn, params, options }) =>
      cachedFetch(type, fetchFn, params, options)
    )
  );

  return requests.reduce((acc, { type }, index) => {
    acc[type] = results[index];
    return acc;
  }, {});
}

/**
 * Prefetch data into cache (useful for preloading)
 */
async function prefetch(type, fetchFn, params = null) {
  // Only prefetch if not already cached
  if (!has(type, params)) {
    return cachedFetch(type, fetchFn, params);
  }
  return get(type, params);
}

/**
 * Update cached data optimistically (for optimistic updates)
 */
function optimisticUpdate(type, params, updateFn) {
  const cached = get(type, params);
  if (cached) {
    const updatedData = updateFn(cached.data);
    set(type, updatedData, params);
    return updatedData;
  }
  return null;
}

/**
 * Get cache statistics (useful for debugging)
 */
function getStats() {
  const stats = {
    memoryEntries: memoryCache.size,
    pendingRequests: pendingRequests.size,
    entries: [],
  };

  for (const [key, entry] of memoryCache.entries()) {
    const isExpired = entry.expiresAt < Date.now();
    stats.entries.push({
      key: key.replace(CACHE_PREFIX, ''),
      type: entry.type,
      isExpired,
      expiresIn: isExpired ? 0 : Math.round((entry.expiresAt - Date.now()) / 1000),
      size: JSON.stringify(entry.data).length,
    });
  }

  return stats;
}

/**
 * Clean up expired entries (called periodically)
 */
function cleanupExpired() {
  const now = Date.now();

  for (const [key, entry] of memoryCache.entries()) {
    if (entry.expiresAt < now) {
      memoryCache.delete(key);
    }
  }

  // Also clean localStorage
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          const entry = JSON.parse(stored);
          if (entry.expiresAt < now) {
            localStorage.removeItem(key);
          }
        }
      }
    }
  } catch (e) {
    // Ignore
  }

  // Clean expired IndexedDB entries
  idbCleanupExpired().catch(() => {});
}

// Run cleanup every 5 minutes
setInterval(cleanupExpired, 5 * 60 * 1000);

/**
 * Clear all application caches on page load
 * This helps resolve hanging loading screens caused by stale cached data.
 * Preserves: cookie consent, i18n settings, Supabase auth tokens, and IndexedDB admin cache
 */
function clearAllOnLoad() {
  console.log('[Cache] Clearing application caches on page load...');

  // 1. Clear in-memory cache (admin entries will rehydrate from IndexedDB on next access)
  memoryCache.clear();
  pendingRequests.clear();

  // 2. Clear localStorage cache entries (cm_cache_ prefix)
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    console.warn('[Cache] localStorage cleanup failed:', e.message);
  }

  // 3. Clear sessionStorage profile caches (cm_profile_cache_ prefix)
  try {
    const PROFILE_CACHE_PREFIX = 'cm_profile_cache_';
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(PROFILE_CACHE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch (e) {
    console.warn('[Cache] sessionStorage cleanup failed:', e.message);
  }

  // 4. Clear currentGarageId to force fresh fetch
  try {
    localStorage.removeItem('currentGarageId');
  } catch (e) {
    // Ignore
  }

  // NOTE: IndexedDB admin cache is NOT cleared here - it survives page loads
  // for instant admin page rendering. It is cleared on logout via clearAll().

  console.log('[Cache] Application caches cleared successfully');
}

// Export cache service
const cacheService = {
  // Core methods
  set,
  get,
  getAsync,
  has,
  invalidate,
  invalidateType,
  clearAll,

  // Fetch methods
  cachedFetch,
  cachedFetchSWR,
  batchFetch,
  prefetch,

  // Update methods
  optimisticUpdate,

  // Utility methods
  getStats,
  cleanupExpired,
  generateKey,
  clearAllOnLoad,
  useIndexedDB,

  // Configuration
  CACHE_CONFIG,
  getTTL,
};

export default cacheService;

// Named exports for convenience
export {
  set,
  get,
  getAsync,
  has,
  invalidate,
  invalidateType,
  clearAll,
  cachedFetch,
  cachedFetchSWR,
  batchFetch,
  prefetch,
  optimisticUpdate,
  getStats,
  clearAllOnLoad,
  useIndexedDB,
  CACHE_CONFIG,
};
